import React from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';

const STATUS_COLORS = {
  accepted: 'purple',
  awaiting_payment: 'yellow',
  paid: 'green',
  planned: 'cyan',
  in_transit: 'cyan',
  pickedUp: 'blue',
  delivered: 'green',
};

function formatDate(dateStr) {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return null;
  }
}

function getNextStatusHint(status) {
  const hints = {
    accepted: 'Plan transport',
    paid: 'Mark as picked up',
    planned: 'Mark as picked up',
    in_transit: 'Mark as delivered',
    pickedUp: 'Mark as delivered',
    delivered: 'Awaiting completion',
  };
  return hints[status] || null;
}

function JobCard({ job, navigate, onTriggerMessage }) {
  const jobId = job.id || job._id;
  const from = job.fromCity || job.fromAddress || '—';
  const to = job.toCity || job.toAddress || '—';
  const status = job.status || 'accepted';
  const pickup = formatDate(job.partnerPickupDate || job.pickupDateFrom);
  const hint = getNextStatusHint(status);

  const handleView = () => {
    navigate(`/partner/listings/${jobId}`);
  };

  const handleUpdateStatus = () => {
    if (!onTriggerMessage) return;
    onTriggerMessage('Update job status', {
      listingId: jobId,
      entityId: jobId,
      entityType: 'listing',
    });
  };

  return (
    <Box borderWidth="1px" borderRadius="xl" p={3} bg="white" borderColor="gray.200">
      <Stack spacing={2}>
        <HStack justify="space-between" align="flex-start">
          <Stack spacing={0} flex={1}>
            <Text fontSize="sm" fontWeight="700" color="navy.700" noOfLines={1}>
              {from} → {to}
            </Text>
            {pickup ? (
              <Text fontSize="xs" color="gray.500">Pickup: {pickup}</Text>
            ) : null}
          </Stack>
          <Badge
            colorScheme={STATUS_COLORS[status] || 'gray'}
            borderRadius="full"
            px={2}
            py={0.5}
            fontSize="10px"
            flexShrink={0}
          >
            {status.replace(/_/g, ' ')}
          </Badge>
        </HStack>

        {hint ? (
          <Text fontSize="xs" color="gray.500" fontStyle="italic">Next: {hint}</Text>
        ) : null}

        <HStack spacing={2}>
          <Button size="xs" colorScheme="blue" onClick={handleView}>
            View job
          </Button>
          {['paid', 'planned', 'in_transit', 'pickedUp'].includes(status) ? (
            <Button size="xs" variant="outline" colorScheme="blue" onClick={handleUpdateStatus}>
              Update status
            </Button>
          ) : null}
        </HStack>
      </Stack>
    </Box>
  );
}

export default function AiJobCards({ result, navigate, onTriggerMessage }) {
  const jobs = result.jobs || [];

  if (!jobs.length) {
    return (
      <Box borderWidth="1px" borderRadius="xl" p={3} bg="gray.50" borderColor="gray.200">
        <Text fontSize="sm" color="gray.500">No active jobs found.</Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={2}>
      <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.15em" color="blue.500">
        {jobs.length} active job{jobs.length !== 1 ? 's' : ''}
      </Text>
      {jobs.map((job) => (
        <JobCard
          key={job.id || job._id}
          job={job}
          navigate={navigate}
          onTriggerMessage={onTriggerMessage}
        />
      ))}
    </VStack>
  );
}
